import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCopy, FaProjectDiagram, FaNetworkWired, FaImage, FaPrint, FaStore, FaWhatsapp } from 'react-icons/fa';

import ConteoCheck from '../ConteoCheck';
import RotacionBadge from '../RotacionBadge';
import NombreProductoTooltip from '../../NombreProductoTooltip';
import copyToClipboard from '../../clipboard';
import { validarUbicacion } from '../../ubicacionValidator';
import formatCurrency from '../../../utils/formatCurrency';
import ProductSalesMetrics from './ProductSalesMetrics';
import SolicitudInternaButton from '../SolicitudInternaButton';

import { getStatusClasses as defaultGetStatusClasses } from '../../../utils/LabelColors';
import { STORE_BASE_URL } from '../../../utils/storeConstants';

const ProductCard = ({
  producto,
  token,
  showActions,
  showPrintLabel,
  onProductSelect,
  onUpdateStock,
  onOpenParentModal,
  onPrintLabel,
  formatAplicaciones = (val) => val || 'General',
  getStatusClasses = defaultGetStatusClasses,
  showPriceOnly = false,
  showStoreLink = false,
  showWhatsAppShare = false,
  showMeliLink = false,
  onShareWhatsApp = null,
  storeBaseUrl = STORE_BASE_URL,
  mostrarInfoInterna = false,
  mostrarMetricasVentas = false,
  showSolicitudInterna = true,
}) => {
  const thumbnailUrl = producto.url;
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleActionClick = (e) => {
    e.stopPropagation();
  };

  const handleButtonClick = (e, callback) => {
    e.preventDefault();
    e.stopPropagation();
    if (callback) callback();
  };

  useEffect(() => {
    if (!isImageModalOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isImageModalOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsImageModalOpen(false);
      }
    };
    if (isImageModalOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImageModalOpen]);

  const handleImageClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsImageModalOpen(true);
  };

  const precioDisplay = producto.rango_precios_hijos && producto.rango_precios_hijos.precio_min !== undefined && producto.rango_precios_hijos.precio_max !== undefined
    ? producto.rango_precios_hijos.precio_min === producto.rango_precios_hijos.precio_max
      ? formatCurrency(producto.rango_precios_hijos.precio_min)
      : `${formatCurrency(producto.rango_precios_hijos.precio_min)} - ${formatCurrency(producto.rango_precios_hijos.precio_max)}`
    : formatCurrency(producto.precio_venta);

  const tieneAplicaciones = producto.aplicaciones_vehiculo
    && String(producto.aplicaciones_vehiculo).trim() !== ''
    && producto.segmento_vehiculo !== 'universal';
  const categoriaDisplay = producto.nombre_categoria || producto.categoria || 'N/A';

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 flex flex-col overflow-hidden">

      <a
        href={`/productos/vista/${producto.id_producto}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-4 flex-grow block group"
      >
        <div className="flex justify-between items-start gap-2">
          <span className="font-semibold text-gray-500 text-xs">SKU: {producto.sku}</span>
          <div className="flex items-center gap-2 shrink-0">
            <RotacionBadge
              estadoRotacion={producto.estado_rotacion}
              ventas30={producto.ventas_30_dias}
              fechaUltimaVenta={producto.fecha_ultima_venta}
              tooltipId={`rot-card-${producto.id_producto}`}
            />
            {producto.es_padre ? (
              <FaNetworkWired className="h-5 w-5 text-yellow-500" title="Producto Padre" />
            ) : (
              <ConteoCheck
                show={producto.tieneMovimientosRecientes}
                empleado={producto.nombre_empleado}
                fecha={producto.fecha_ultimo_movimiento}
                tooltipId={`conteo-card-${producto.id_producto}`}
              />
            )}
          </div>
        </div>

        <h4 className="text-base sm:text-lg font-bold text-gray-900 my-2 line-clamp-2 group-hover:text-yellow-700 transition-colors">
          <NombreProductoTooltip nombreProducto={producto.nombre_producto} idProducto={producto.id_producto} />
        </h4>

        <div className="flex flex-col-reverse sm:flex-row items-start mb-3 gap-3">
          <div className="flex-grow min-w-0 space-y-0.5">
            <p className="text-xs text-gray-500">
              <span className="font-semibold text-gray-600">Cód. Barras:</span> {producto.codigo_barras}
              <button
                onClick={(e) => handleButtonClick(e, () => copyToClipboard(producto.codigo_barras))}
                className="ml-2 text-blue-500 hover:text-blue-700 inline-block align-middle"
                title="Copiar Código de Barras"
              >
                <FaCopy />
              </button>
            </p>
            <p className="text-xs text-gray-500"><span className="font-semibold text-gray-600">Marca:</span> {producto.nombre_marca}</p>
            <p className="text-xs text-gray-500"><span className="font-semibold text-gray-600">Medidas:</span> {producto.medidas || 'N/A'}</p>
            <p className="text-xs text-gray-500"><span className="font-semibold text-gray-600">Ubicación:</span> {validarUbicacion(producto.ubicacion)}</p>
            <p className="text-xs text-gray-500"><span className="font-semibold text-gray-600">Categoría:</span> {categoriaDisplay}</p>
            {mostrarInfoInterna && (
              <p className="text-xs text-gray-500"><span className="font-semibold text-gray-600">Proveedor:</span> {producto.nombre_proveedor || 'N/A'}</p>
            )}

            {producto.atributos_tecnicos && producto.atributos_tecnicos.length > 0 && (
              <div className="mt-2 pt-2 border-t border-dashed border-sky-200">
                {producto.atributos_tecnicos.map((attr, index) => (
                  attr.valor ? (
                    <p key={index} className="text-xs text-sky-700 italic">
                      <span className="font-semibold">{attr.nombre}:</span> {attr.valor}
                    </p>
                  ) : null
                ))}
              </div>
            )}
          </div>

          <div
            className={`flex-shrink-0 w-32 h-32 sm:w-36 sm:h-36 relative cursor-pointer z-10 ${!thumbnailUrl || imageError ? 'pointer-events-none' : ''}`}
            onClick={!imageError && thumbnailUrl ? handleImageClick : undefined}
            title={!imageError && thumbnailUrl ? 'Ver imagen' : 'Sin imagen disponible'}
          >
            {!imageError && thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={`Miniatura de ${producto.nombre_producto}`}
                onError={() => setImageError(true)}
                className="w-full h-full object-contain rounded-xl border border-gray-200 bg-gray-50"
              />
            ) : (
              <div className="w-full h-full bg-gray-50 flex items-center justify-center rounded-xl border border-gray-200">
                <FaImage className="w-10 h-10 text-yellow-500" />
              </div>
            )}
          </div>
        </div>

        {(showActions || showPriceOnly) && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            {showActions && (
              <>
                {tieneAplicaciones && (
                  <p className="text-xs text-gray-500 truncate mb-2" title={formatAplicaciones(producto.aplicaciones_vehiculo)}>
                    <span className="font-semibold text-gray-600">Aplicación:</span> {formatAplicaciones(producto.aplicaciones_vehiculo)}
                  </p>
                )}
                <div className="bg-gray-50 rounded-xl p-3 grid grid-cols-3 gap-2">
                  <div className="text-center flex flex-col items-center">
                    <p className="text-[10px] uppercase font-semibold text-gray-500 mb-1">Stock</p>
                    {!producto.es_padre ? (
                      <button
                        type="button"
                        onClick={(e) => handleButtonClick(e, () => onUpdateStock(producto))}
                        className="min-w-[3rem] px-3 py-1.5 rounded-lg text-sm font-bold bg-gray-900 text-yellow-400 hover:bg-yellow-500 hover:!text-gray-900 transition-colors shadow-sm"
                        title="Click para actualizar stock"
                      >
                        {parseFloat(producto.existencias).toFixed(1)}
                      </button>
                    ) : (
                      <span className="inline-block min-w-[3rem] px-3 py-1.5 rounded-lg text-sm font-bold bg-gray-200 text-gray-500 cursor-not-allowed">
                        {parseFloat(producto.existencias).toFixed(1)}
                      </span>
                    )}
                  </div>
                  <div className="text-center col-span-2">
                    <p className="text-[10px] uppercase font-semibold text-gray-500">Precio</p>
                    <p className="text-sm font-bold text-green-700 mt-0.5">{precioDisplay}</p>
                  </div>
                </div>
                {mostrarMetricasVentas && <ProductSalesMetrics producto={producto} compact />}
                <p className="text-xs text-gray-400 mt-2 text-center">Stock mín: {producto.stock_minimo}</p>
              </>
            )}
            {!showActions && showPriceOnly && (
              <div className="text-center">
                <div className="text-sm text-green-600 font-bold">{precioDisplay}</div>
              </div>
            )}
          </div>
        )}
      </a>

      <div className={`p-3 flex items-center justify-between border-t ${getStatusClasses(producto.estado)}`}>
        <div className="flex items-center gap-3">
          {producto.es_padre && onOpenParentModal && (
            <button onClick={(e) => { handleActionClick(e); onOpenParentModal(producto); }} className="text-white hover:opacity-80" title="Ver Hijos">
              <FaProjectDiagram className="w-5 h-5" />
            </button>
          )}
          {showPrintLabel && onPrintLabel && (
            <button onClick={(e) => { handleActionClick(e); onPrintLabel(producto); }} className="text-white hover:opacity-80" title="Imprimir Etiqueta">
              <FaPrint className="w-5 h-5" />
            </button>
          )}

          {showSolicitudInterna && token && (
            <SolicitudInternaButton
              token={token}
              idProducto={producto.id_producto}
              esPadre={Boolean(producto.es_padre)}
              nombreProducto={producto.nombre_producto}
              tone="light"
            />
          )}

          {(showStoreLink || showMeliLink || showWhatsAppShare) && (
            <>
              <div className="h-4 w-[1px] bg-white/30 mx-0.5" />
              {showStoreLink && (
                <a
                  href={`${storeBaseUrl}/productos/${producto.id_producto}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleActionClick}
                  className="text-white hover:opacity-80 transition-all font-bold"
                  title="Ver en Tienda Virtual"
                >
                  <FaStore className="w-5 h-5" />
                </a>
              )}
              {showMeliLink && (
                <button
                  onClick={async (e) => {
                    handleActionClick(e);
                    if (producto.enlace_meli) {
                      window.open(producto.enlace_meli, '_blank');
                    } else {
                      try {
                        const { default: axios } = await import('axios');
                        const res = await axios.get(`/api/productos/meli-link/${producto.id_producto}`);
                        if (res.data.status === 'success') {
                          window.open(res.data.enlace_meli, '_blank');
                        } else {
                          const { default: Swal } = await import('sweetalert2');
                          Swal.fire({
                            icon: 'info',
                            title: 'No Publicado',
                            text: 'Este producto no tiene una publicación activa en MercadoLibre.',
                            confirmButtonColor: '#ffc240',
                            confirmButtonText: 'Entendido'
                          });
                        }
                      } catch (err) {
                        console.error('Error fetching meli link:', err);
                      }
                    }
                  }}
                  className="hover:scale-110 transition-all w-6 h-6 flex items-center justify-center p-0.5 bg-white rounded-full overflow-hidden"
                  title="Ver en MercadoLibre"
                >
                  <img src="/images/mercado-libre.png" alt="Meli Logo" className="w-full h-full object-contain" />
                </button>
              )}
              {showWhatsAppShare && onShareWhatsApp && (
                <button
                  onClick={(e) => { handleActionClick(e); onShareWhatsApp(producto); }}
                  className="text-white hover:opacity-80 transition-all"
                  title="Compartir por WhatsApp"
                >
                  <FaWhatsapp className="w-5 h-5" />
                </button>
              )}
            </>
          )}
        </div>
        <span className="ml-4 font-semibold text-sm hidden sm:inline">{producto.estado}</span>
        {onProductSelect && (
          <button
            onClick={(e) => { handleActionClick(e); onProductSelect(producto); }}
            className="bg-yellow-500 text-black px-4 py-2 rounded-lg hover:bg-black hover:text-white font-bold text-sm transition-colors duration-200"
          >
            Usar Producto
          </button>
        )}
      </div>

      {isImageModalOpen && createPortal(
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 p-2 sm:p-4 backdrop-blur-sm"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div className="relative flex max-h-[95vh] max-w-[95vw] items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setIsImageModalOpen(false)}
              className="absolute -top-2 -right-2 z-[100000] flex h-12 w-12 items-center justify-center rounded-full border-2 border-black/10 bg-white text-3xl font-bold text-black shadow-2xl transition-all hover:bg-gray-200 sm:top-4 sm:right-4"
              title="Cerrar vista previa"
            >
              &times;
            </button>
            <img
              src={thumbnailUrl}
              alt={`Vista previa de ${producto.nombre_producto}`}
              className="max-h-[90vh] max-w-full rounded-xl border-4 border-white/10 object-contain shadow-2xl"
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProductCard;
